import { Controller } from '@hotwired/stimulus';
import { Dropdown } from 'bootstrap';

const templates = {
    /**
     * Important! Changes to the menu DOM structure must also be adopted to
     * templates/themes/wieland/element/contactform.html.twig
     */
    tab: {
        template: `
            <input
                class="contact-form-radio-button sr-only"
                id="{{ type }}"
                type="radio"
                name="type"
                value="{{ type }}"
                required
                {{ checked }}
            >
            <label for="{{ type }}"
            data-action="click->contact#changeContactPersonType" data-contact-type-param="{{ type }}"
            >{{ label }}</label>
        `,
    },
    dropdown: {
        template: `
             <div
                class="contact-form-contact-person {{ is-active }}"
                data-type="{{ type }}"
                data-contact-target="contactPersonWrapper"
             >
                <div class="dropdown contact-dropdown">
                    <div class="contact-dropdown-toggle" data-action="click->contact#toggle" data-bs-toggle="dropdown" data-bs-display="static">
                        <div
                            data-contact-target="contactPersonToggle"
                            data-type="{{ type }}"
                        >
                            <img
                                 src="/build/images/placeholder/wieland-logo.png"
                                 loading="lazy"
                                 alt="Wieland logo placeholder" width="40" height="40">
                            <span>{{ dropdownToggle }}</span>
                        </div>
                        <i class="fa-light fa-angle-down"></i>
                    </div>
                    <ul class="dropdown-menu">
                        {{ contactList }}
                    </ul>
                </div>
                <select
                    class="sr-only d-none"
                    name="contact_id"
                    {{ required }}
                ></select>
            </div>
        `,
    },
    contactItem: {
        template: `
            <li>
                <div class="dropdown-item"
                data-action="click->contact#selectContactPerson"
                data-contact-contact_id-param="{{ contactId }}"
                data-contact-type-param="{{ type }}"
                >
                    <div class="contact-person contact-person-line">
                        <p class="contact-person-description">
                            <img class="image"
                                src="/build/images/placeholder/wieland-logo.png"
                                loading="lazy"
                                alt="Wieland logo placeholder" width="40" height="40">
                            <strong>{{ b2bUnitName }}</strong>
                            <br>
                            <span>{{ firstName }} {{ lastName }}</span>
                            <br>
                            <a class="phone" href="tel:{{ phone }}">{{ phone }}</a>
                            <a class="email" href="#">{{ email }}</a>
                        </p>
                    </div>
                </div>
            </li>
        `,
    },
};


export default class extends Controller {
    static targets = [
        'otherContacts',

        'message',
        'spamProtectionToken',
        'spamProtectionGenerated',
        'spamProtectionEventCount',

        'contactForm',
        'contactFormRadioButtons',
        'contactFormDropdowns',
        'dynamicHiddenFormData',
        'formSubmitButton',
        'contactSent',
        'contactError',

        'contactPersonWrapper',
        'contactPersonToggle',
        'careerNotice',
        'phone',
        'websiteField'
    ];

    static outlets = [
        'mywieland',
        'mywielandold'
    ];

    static values = {
        translations: Object
    };

    #isUserLoggedIn = false;
    #myWielandUserInfo = null;
    #myWielandContactData = null;
    #myWielandTabsConfig = null;

    connect() {
        this.#showLinkOtherContactPersons();
        this.#prepareContactForm();
        this.#checkUrlAndOpenPopup();
        this.#closeModal();
        this.#showEncryptedEmails();
        $(this.phoneTarget).on("input", this.#sanitizePhoneInput.bind(this));
        const updateUserState = () => {
            const mywielandController = this.hasMywielandOutlet ? this.mywielandOutlet : 
                                       (this.hasMywielandoldOutlet ? this.mywielandoldOutlet : null);
            if (mywielandController && mywielandController.isUserLoggedIn()) {
                this.#renderLoggedInContactForm();
            }
        };

        $('body').on('mywieland:contactdata:updated', updateUserState);
        updateUserState();
    }

    sendContactForm(event) {
        event.preventDefault();
        
        // Check honeypot field value to prevent spam
        if (this.hasWebsiteFieldTarget && this.websiteFieldTarget.value) {
            return;
        }
        const submitButton = this.formSubmitButtonTarget;
        submitButton.disabled = true;

        // In template .sr-only inputs are included in form data but not visible
        const formData = new FormData(this.contactFormTarget);
        const baseUrl = document.body.getAttribute('data-base-url');
        const ajaxUrl = `${baseUrl}/en/contact-form/submit`;

        fetch(ajaxUrl, {
            method: 'POST',
            body: formData
        })
            .then(response => response.json())
            .then(data => {
                if (data.status === 'OK') {
                    this.contactSentTarget.classList.remove('d-none');
                } else {
                    this.contactErrorTarget.classList.remove('d-none');
                }
            })
            .catch(() => {
                this.contactErrorTarget.classList.remove('d-none');
            })
            .finally(() => {
                this.contactFormTarget.classList.add('d-none');
                submitButton.disabled = false;
                this.contactFormTarget.reset();
            });
    }

    changeContactPersonType(event) {
        const selectedContactType = event.params.type;

        // Check if the selected contact type is 'career'
        if (selectedContactType === 'contact_career') {
            this.careerNoticeTarget.classList.remove('d-none');
        } else {
            this.careerNoticeTarget.classList.add('d-none');
        }

        this.contactPersonWrapperTargets.forEach((contactPersonWrapper) => {
            const selectElement = $(contactPersonWrapper).find('select');

            // Hide or show the dropdown based on the selected contact type
            if (contactPersonWrapper.dataset.type === selectedContactType) {
                contactPersonWrapper.classList.add('is-active');
                // Set the select element to be required and included in the form submission
                selectElement.attr('required', 'required').attr('name', 'contact_id');

                // If user is logged in, we need to add hidden fields with contact data
                if(this.#isUserLoggedIn) {
                    this.#addHiddenFormData(selectedContactType, selectElement.val());
                }
            } else {
                contactPersonWrapper.classList.remove('is-active');
                // Reset the select element to exclude it from the form submission
                selectElement.removeAttr('required').removeAttr('name');
            }
        });
    }

    selectContactPerson(event) {
        this.#selectContactPerson(
            event.currentTarget,
            event.params.type,
            String(event.params.contactId)
        );
    }

    #private

    #selectContactPerson(contactElement, selectedContactType, contactId) {
        if (!contactElement) {
            return;
        }

        const selectedContactPerson = contactElement.cloneNode(true);
        this.contactPersonToggleTargets.forEach((contactPersonToggle) => {
            if (contactPersonToggle.dataset.type === selectedContactType) {
                contactPersonToggle.replaceChildren(
                    ...selectedContactPerson.cloneNode(true).childNodes
                );
            }
        });

        this.contactPersonWrapperTargets.forEach((contactPersonWrapper) => {
            if (contactPersonWrapper.dataset.type === selectedContactType) {
                const selectElement = $(contactPersonWrapper).find('select');
                selectElement.append(new Option("", contactId));
                selectElement.val(contactId);
            }
        });

        if(this.#isUserLoggedIn) {
            this.#addHiddenFormData(selectedContactType, contactId);
        }
    }

    #renderLoggedInContactForm() {
        if (this.#isUserLoggedIn) {
            return;
        }

        const myWielandContactDataString = localStorage.getItem('myWielandContactData');
        const myWielandUserInfoString = localStorage.getItem('userInfo');
        if (myWielandContactDataString === null || myWielandUserInfoString === null) {
            return;
        }

        let myWielandContactData;
        let myWielandUserInfo;
        try {
            myWielandContactData = JSON.parse(myWielandContactDataString);
            myWielandUserInfo = JSON.parse(myWielandUserInfoString);
        } catch {
            return;
        }

        if (!Array.isArray(myWielandContactData) || !myWielandUserInfo) {
            return;
        }

        const contactTypes = ['salesRep', 'customerService', 'technicalService'];
        const isUsableContact = (contact) => contact
          && ['id', 'firstName', 'lastName', 'email'].every((key) => contact[key]);
        const normalizedContactData = myWielandContactData.map((contactGroup) => {
            const group = contactGroup || {};
            return {
                b2bUnitName: group.b2bUnitName,
                salesRep: Array.isArray(group.salesRep) ? group.salesRep.filter(isUsableContact) : [],
                customerService: Array.isArray(group.customerService) ? group.customerService.filter(isUsableContact) : [],
                technicalService: Array.isArray(group.technicalService) ? group.technicalService.filter(isUsableContact) : []
            };
        });
        const hasAssignedContacts = normalizedContactData.some((contactGroup) =>
            contactTypes.some((type) => contactGroup[type].length > 0)
        );
        const hasRequiredUserInfo = [
            'customerID',
            'firstName',
            'lastName',
            'languageISOCode',
            'email',
            'defaultB2BUnitName'
        ].every((key) => myWielandUserInfo[key]);

        if (!hasAssignedContacts || !hasRequiredUserInfo) {
            return;
        }

        this.#myWielandContactData = normalizedContactData;
        this.#myWielandUserInfo = myWielandUserInfo;

        if (!this.#renderLoggedInTabs(this.#myWielandContactData)) {
            return;
        }

        const [firstContactType] = Object.keys(this.#myWielandTabsConfig);
        const firstContact = this.#myWielandTabsConfig[firstContactType]?.contacts[0];
        const firstContactElement = firstContactType
            ? this.contactFormDropdownsTarget.querySelector(
                `[data-type="${firstContactType}"] .dropdown-item`
            )
            : null;
        if (!firstContact || !firstContactElement) {
            return;
        }

        this.#isUserLoggedIn = true;
        $(this.contactFormTarget).find(
          "input[name='firstname'], " +
          "input[name='lastname'], " +
          "input[name='country'], " +
          "input[name='phone'], " +
          "input[name='email'], " +
          "input[name='fromCompany']"
        ).remove();
        this.contactFormTarget.classList.add('loggedin');
        this.#selectContactPerson(
            firstContactElement,
            firstContactType,
            String(firstContact.contact.id)
        );
    }

    #renderLoggedInTabs(myWielandContactData) {
        const tabConfigs = {
            salesRep: {
                label: this.translationsValue.salesTab,
                contacts: []
            },
            customerService: {
                label: this.translationsValue.customerServiceTab,
                contacts: []
            },
            technicalService: {
                label: this.translationsValue.technicalServiceTab,
                contacts: []
            }
        };

        // Prepare data for tabs from myWielandContactData
        myWielandContactData.forEach(({ b2bUnitName, salesRep, customerService, technicalService }) => {
            Object.entries({ salesRep, customerService, technicalService }).forEach(([key, contacts]) => {
                if (contacts.length > 0) {
                    tabConfigs[key].contacts.push({
                        b2bUnitName,
                        contact: contacts[0]
                    });
                }
            });
        });

        // Filter out empty tabs
        Object.keys(tabConfigs).forEach(key => {
            if (tabConfigs[key].contacts.length === 0) {
                delete tabConfigs[key];
            }
        });

        this.#myWielandTabsConfig = tabConfigs;

        const tabsHtml = Object.entries(tabConfigs).map(([type, { label, contacts }], index) => {
            if (contacts.length === 0) return '';
            const existingTab = this.contactFormRadioButtonsTarget.querySelector(`input[id="${type}"]`);
            if (existingTab) return '';
            return templates.tab.template
              .replace(/{{ type }}/g, type)
              .replace('{{ label }}', label)
              .replace('{{ checked }}', index === 0 ? 'checked' : '');
        }).join('');
        const dropdownsHtml = Object.entries(tabConfigs).map(([type, { contacts }], index) => {
            if (contacts.length === 0) return '';
            const existingDropdown = this.contactFormDropdownsTarget.querySelector(`[data-type="${type}"]`);
            if (existingDropdown) return '';

            const contactListHtml = contacts.map(({ b2bUnitName, contact }) => {
                return templates.contactItem.template
                  .replace('{{ contactId }}', contact.id)
                  .replace(/{{ type }}/g, type)
                  .replace('{{ b2bUnitName }}', b2bUnitName)
                  .replace('{{ firstName }}', contact.firstName)
                  .replace('{{ lastName }}', contact.lastName)
                  .replace(/{{ phone }}/g, contact.phone ?? '')
                  .replace('{{ email }}', contact.email);
            }).join('');

            return templates.dropdown.template
              .replace(/{{ type }}/g, type)
              .replace('{{ is-active }}', index === 0 ? 'is-active' : '')
              .replace('{{ required }}', index === 0 ? 'required' : '')
              .replace('{{ contactList }}', contactListHtml)
              .replace('{{ dropdownToggle }}', this.translationsValue.dropdownToggle);
        }).join('');

        if (!tabsHtml || !dropdownsHtml.includes('dropdown-item')) {
            return false;
        }

        this.contactFormRadioButtonsTarget.querySelectorAll('input').forEach((input) => {
            input.removeAttribute('checked');
        });

        $(this.contactFormRadioButtonsTarget)
          .find('input[id="contact_general_contact"], input[id="contact_products"], label[for="contact_general_contact"], label[for="contact_products"]')
          .remove();

        this.contactFormRadioButtonsTarget.innerHTML = tabsHtml + this.contactFormRadioButtonsTarget.innerHTML;

        this.contactFormDropdownsTarget.querySelectorAll('[data-contact-target="contactPersonWrapper"]').forEach((contactPersonWrapper) => {
            contactPersonWrapper.classList.remove('is-active');
        });

        this.contactFormDropdownsTarget.innerHTML = dropdownsHtml + this.contactFormDropdownsTarget.innerHTML;

        return true;
    }

    #showLinkOtherContactPersons() {
        const contactsElement = document.getElementById('contacts');
        if (this.hasOtherContactsTarget && contactsElement !== null) {
            this.otherContactsTarget.classList.remove('d-none');
        }
    }

    #prepareContactForm() {
        if (this.hasMessageTarget &&
            this.hasSpamProtectionTokenTarget &&
            this.hasSpamProtectionGeneratedTarget &&
            this.hasSpamProtectionEventCountTarget) {
            this.messageTarget.addEventListener('keyup', () => {
                this.spamProtectionGeneratedTarget.value = this.spamProtectionTokenTarget.value;
                this.spamProtectionEventCountTarget.value = parseInt(this.spamProtectionEventCountTarget.value) + 1;
            });
        }
    }

    #addHiddenFormData(selectedContactType, contactId) {
        this.dynamicHiddenFormDataTarget.innerHTML = '';

        if(selectedContactType && contactId) {
            const contactPerson = this.#myWielandTabsConfig?.[selectedContactType]?.contacts.find(
                contact => String(contact.contact.id) === String(contactId)
            );

            if (contactPerson) {
                $('<input>', {
                    type: 'hidden',
                    name: 'contact_b2b_unit_name',
                    value: contactPerson.b2bUnitName
                }).appendTo(this.dynamicHiddenFormDataTarget);

                $('<input>', {
                    type: 'hidden',
                    name: 'contact_email',
                    value: contactPerson.contact.email
                }).appendTo(this.dynamicHiddenFormDataTarget);

                $('<input>', {
                    type: 'hidden',
                    name: 'contact_phone',
                    value: contactPerson.contact.phone ?? ''
                }).appendTo(this.dynamicHiddenFormDataTarget);

                $('<input>', {
                    type: 'hidden',
                    name: 'contact_first_name',
                    value: contactPerson.contact.firstName
                }).appendTo(this.dynamicHiddenFormDataTarget);

                $('<input>', {
                    type: 'hidden',
                    name: 'contact_last_name',
                    value: contactPerson.contact.lastName
                }).appendTo(this.dynamicHiddenFormDataTarget);
            }
        }

        const userInfo = this.#myWielandUserInfo;
        if(userInfo) {
            $('<input>', {
                type: 'hidden',
                name: 'customer_id',
                value: userInfo.customerID
            }).appendTo(this.dynamicHiddenFormDataTarget);
            $('<input>', {
                type: 'hidden',
                name: 'firstname',
                value: userInfo.firstName
            }).appendTo(this.dynamicHiddenFormDataTarget);
            $('<input>', {
                type: 'hidden',
                name: 'lastname',
                value: userInfo.lastName
            }).appendTo(this.dynamicHiddenFormDataTarget);
            $('<input>', {
                type: 'hidden',
                name: 'country',
                value: userInfo.languageISOCode
            }).appendTo(this.dynamicHiddenFormDataTarget);
            $('<input>', {
                type: 'hidden',
                name: 'email',
                value: userInfo.email
            }).appendTo(this.dynamicHiddenFormDataTarget);
            $('<input>', {
                type: 'hidden',
                name: 'fromCompany',
                value: userInfo.defaultB2BUnitName
            }).appendTo(this.dynamicHiddenFormDataTarget);
        }
    }

    #checkUrlAndOpenPopup() {
        if (['#contact', '#kontakt'].includes(window.location.hash)) {
            $(this.element).modal('show');
        }

        // Automatically click the "Career" tab if it exists for unlogged users
        const careerTabLabel = this.contactFormRadioButtonsTarget.querySelector('label[for="contact_career"]');
        // Do not show the "Career" tab by default, if the user is not on a career related page
        const isCareerPage = location.pathname.indexOf('/karriere') > -1 || location.pathname.indexOf('/career') > -1;
        if (isCareerPage && careerTabLabel) {
            careerTabLabel.click();
        }
    }

    #closeModal() {
        $(this.element).on('hidden.bs.modal', () => {
            this.contactSentTarget.classList.add('d-none');
            this.contactErrorTarget.classList.add('d-none');
            this.contactFormTarget.classList.remove('d-none');
        });
    }

    #showEncryptedEmails() {
        // For each encrypted email link, decrypt the email and set the href attribute
        $(this.element).find('a[data-encrypted-mail]').each((index, element) => {
            const encrypted = element.getAttribute('data-encrypted-mail');
            const base64 = encrypted.split('').reverse().join('');
            const email = atob(base64);
            element.setAttribute('href', 'mailto:' + email);
            element.textContent = email;
        });
    }

    #sanitizePhoneInput() {
        const cleanedValue = this.phoneTarget.value.replace(/[^\d+\-\s]/g, '');
        this.phoneTarget.value=cleanedValue;
    }

    toggle(event) {
        event.preventDefault();

        const target = event.currentTarget;

        const bsDropdown = Dropdown.getOrCreateInstance(target);
        bsDropdown.toggle();
    }
}
